/**
 * ImportPhotosDialog — batch upload up to 100 product photos, item_number
 * derived from filename, optional AI-generated description via
 * ``POST /api/product-overview/import``.
 *
 * Flow: user pre-sets ``photo_type`` / ``buyer`` / ``category`` at the top,
 * drops files into the drop-zone (or picks via <input>), previews each row
 * (with an override input for item_number), then clicks Import to fire the
 * multipart POST.  Server-side per-file errors surface as toast messages.
 */
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, X, Loader2, ImagePlus, Trash2, Camera, Images } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const MAX_FILES = 100;
const ACCEPTED = ["image/jpeg", "image/png", "image/webp", "image/heic"];

const stripExt = (n) => (n || "").replace(/\.[^.]+$/, "").trim();

export function ImportPhotosDialog({ open, onOpenChange, options, onImported }) {
  const { t } = useLanguage();
  const [files, setFiles] = useState([]);           // [{file, item_number, previewUrl}]
  const [presets, setPresets] = useState({ photo_type: "", buyer: "", category: "" });
  const [aiDescribe, setAiDescribe] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const cameraRef = useRef(null);

  const addFiles = useCallback((incoming) => {
    const arr = Array.from(incoming || []);
    const usable = arr.filter((f) => ACCEPTED.some((t) => f.type.startsWith(t.split("/")[0]) || f.name.match(/\.(jpe?g|png|webp|heic)$/i)));
    if (usable.length !== arr.length) {
      toast.warning(`${arr.length - usable.length} ${t("po.import.unsupportedSkipped") || "个不支持的文件已跳过"}`);
    }
    setFiles((prev) => {
      const combined = [...prev, ...usable.map((f) => ({
        file: f,
        item_number: stripExt(f.name),
        previewUrl: URL.createObjectURL(f),
      }))];
      if (combined.length > MAX_FILES) {
        toast.warning(`${t("po.import.tooMany") || "单次最多"} ${MAX_FILES} ${t("po.import.filesUnit") || "张，已截断"}`);
        return combined.slice(0, MAX_FILES);
      }
      return combined;
    });
  }, [t]);

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploading) return;
    addFiles(e.dataTransfer.files);
  };

  const removeFile = (idx) => {
    setFiles((prev) => {
      const item = prev[idx];
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const clearAll = () => {
    files.forEach((f) => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
    setFiles([]);
  };

  const submit = async () => {
    if (files.length === 0) {
      toast.error(t("po.import.pickFirst") || "请先选择或拖入图片");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f.file, `${f.item_number || stripExt(f.file.name)}.${f.file.name.split(".").pop()}`));
      fd.append("photo_type", presets.photo_type || "");
      fd.append("buyer", presets.buyer || "");
      fd.append("category", presets.category || "");
      fd.append("ai_describe", aiDescribe ? "true" : "false");
      const { data } = await api.post("/product-overview/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300_000,  // AI descriptions add per-file latency
      });
      toast.success(`${t("po.import.done") || "已导入"} ${data.imported} ${t("po.records") || "条"}`);
      if (data.errors?.length) {
        toast.warning(`${data.errors.length} ${t("po.import.partialFail") || "个文件失败"}`);
      }
      clearAll();
      onImported?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("po.import.failed") || "导入失败");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !uploading && onOpenChange(o)}>
      <DialogContent className="max-w-3xl" data-testid="po-import-dialog">
        <DialogHeader>
          <DialogTitle>{t("po.import.title") || "导入产品图片"}</DialogTitle>
          <DialogDescription>
            {t("po.import.hint") || `单次最多 ${MAX_FILES} 张。产品编号自动从文件名读取，可在下方逐行修改；描述由 AI 从图片生成。`}
          </DialogDescription>
        </DialogHeader>

        {/* Pre-set metadata applied to the whole batch */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">{t("po.photo_type") || "图片类型"}</Label>
            <Select value={presets.photo_type || "__none__"}
                    onValueChange={(v) => setPresets((p) => ({ ...p, photo_type: v === "__none__" ? "" : v }))}
                    disabled={uploading}>
              <SelectTrigger data-testid="po-import-photo-type" className="h-8 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {(options?.photo_type || []).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("po.buyer") || "采购"}</Label>
            <Select value={presets.buyer || "__none__"}
                    onValueChange={(v) => setPresets((p) => ({ ...p, buyer: v === "__none__" ? "" : v }))}
                    disabled={uploading}>
              <SelectTrigger data-testid="po-import-buyer" className="h-8 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {(options?.buyer || []).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("po.category") || "分类"}</Label>
            <Select value={presets.category || "__none__"}
                    onValueChange={(v) => setPresets((p) => ({ ...p, category: v === "__none__" ? "" : v }))}
                    disabled={uploading}>
              <SelectTrigger data-testid="po-import-category" className="h-8 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {(options?.category || []).map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none">
          <Checkbox checked={aiDescribe} onCheckedChange={(v) => setAiDescribe(!!v)} data-testid="po-import-ai" disabled={uploading} />
          {t("po.import.aiDescribe") || "自动 AI 生成产品描述（可能会略微延长导入时间）"}
        </label>

        {/* Mobile — camera / gallery shortcuts (hidden on >= sm) */}
        <div className="grid grid-cols-2 gap-2 sm:hidden">
          <Button variant="outline" className="h-10" disabled={uploading}
                  onClick={() => cameraRef.current?.click()} data-testid="po-import-camera-btn">
            <Camera size={15} className="mr-1.5" /> {t("po.import.takePhoto") || "拍照"}
          </Button>
          <Button variant="outline" className="h-10" disabled={uploading}
                  onClick={() => inputRef.current?.click()} data-testid="po-import-gallery-btn">
            <Images size={15} className="mr-1.5" /> {t("po.import.fromGallery") || "从相册选择"}
          </Button>
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
            data-testid="po-import-camera-input"
          />
        </div>

        {/* Drop zone */}
        <div
          className="hidden sm:block border-2 border-dashed border-border rounded-md p-6 text-center bg-secondary/30 hover:border-brand/60 transition-colors cursor-pointer"
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          onClick={() => !uploading && inputRef.current?.click()}
          data-testid="po-import-dropzone"
        >
          <UploadCloud size={26} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-sm font-medium">{t("po.import.dropHint") || "拖入图片或点击选择"}</p>
          <p className="text-[11px] text-muted-foreground mt-1">
            JPG · PNG · WebP · HEIC · {t("po.import.maxHint") || `最多 ${MAX_FILES} 张`}
          </p>
        </div>

        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*,.heic,.heif"
          className="hidden"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
          data-testid="po-import-file-input"
        />

        {/* Preview grid — each row's item_number is editable so users can fix
            filename derivations that don't match the intended product code. */}
        {files.length > 0 && (
          <div className="max-h-[45vh] overflow-y-auto border border-border rounded-md">
            <div className="sticky top-0 bg-secondary px-2 py-1 flex items-center justify-between text-[11px] font-semibold">
              <span>{files.length} / {MAX_FILES} {t("po.import.filesUnit") || "张"}</span>
              <button className="text-muted-foreground hover:text-destructive inline-flex items-center gap-1"
                      onClick={clearAll} disabled={uploading} data-testid="po-import-clear">
                <Trash2 size={12} /> {t("po.import.clearAll") || "全部清空"}
              </button>
            </div>
            {files.map((f, idx) => (
              <div key={`${f.file.name}-${idx}`} className="flex items-center gap-2 px-2 py-1.5 border-b border-border last:border-0">
                <img src={f.previewUrl} alt="" className="w-10 h-10 object-cover rounded-sm border border-border" />
                <div className="flex-1 min-w-0">
                  <Input
                    value={f.item_number}
                    onChange={(e) => setFiles((prev) => prev.map((it, i) => i === idx ? { ...it, item_number: e.target.value } : it))}
                    className="h-7 text-xs font-mono"
                    disabled={uploading}
                    data-testid={`po-import-item-${idx}`}
                  />
                  <p className="text-[10px] text-muted-foreground truncate">{f.file.name} · {(f.file.size / 1024).toFixed(0)} KB</p>
                </div>
                <button className="text-muted-foreground hover:text-destructive p-1"
                        onClick={() => removeFile(idx)} disabled={uploading}
                        data-testid={`po-import-remove-${idx}`}>
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
            {t("common.cancel") || "取消"}
          </Button>
          <Button onClick={submit} disabled={uploading || files.length === 0} data-testid="po-import-submit">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
            {uploading ? (t("po.import.uploading") || "导入中…") : `${t("po.import.submit") || "导入"} (${files.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
